<script setup lang="ts">
import { useRoute } from 'vue-router';
import { computed } from 'vue';
import type FlightSchoolCourse from '@/types/FlightSchoolCourse';
import { CourseTypeLabel, type CourseTypes } from '@/types/FlightSchoolCourse';
import type FlightSchool from '@/types/FlightSchool';
import FsumNavButton from '@/components/Navigation/FsumNavButton.vue';
import type { AvailableParts } from '@/types/Certificate';
import { usePartStore } from '@/stores/part';

const props = defineProps<{
    currentSchool: FlightSchool,
    currentCourse: FlightSchoolCourse|null,
}>();

const currentRoute = useRoute();
const partStore = usePartStore();

const currentPart = computed<AvailableParts>(() => partStore.getPartOrUseDefault(props.currentSchool));

const coursesByPart = computed<{ [key in AvailableParts]: FlightSchoolCourse[] }>(() => (
    {
        141: props.currentSchool.courses.filter((course) => course.certificate.part === 141),
        61: props.currentSchool.courses.filter((course) => course.certificate.part === 61),
    }
));

const isCurrentCourse = (courseAcronym: string): boolean => {
    if (props.currentCourse !== null) {
        return courseAcronym.toLowerCase() === props.currentCourse.certificate.acronym.toLowerCase();
    }

    return currentRoute.params.courseAcronym as string === courseAcronym.toLowerCase();
};

const isCurrentRating = (ratingAcronym: string): boolean => {
    if (props.currentCourse !== null) {
        return ratingAcronym.localeCompare(
            currentRoute.params.ratingAcronym as string,
            'en',
            { sensitivity: 'base' },
        ) === 0;
    }

    return false;
};

const isCanadianCourse = (course: FlightSchoolCourse|null): boolean => course?.certificate.acronym.toLowerCase().includes('-ca') ?? false;

const splitCourses = computed<CourseTypes[]>(() => {
    if (currentPart.value === 141) {
        return [
            {
                label: CourseTypeLabel.PILOT_CERTIFICATIONS,
                courses: coursesByPart.value[currentPart.value],
                icon: 'sym_o_flight_takeoff',
            },
        ];
    }

    const canadianCourses: FlightSchoolCourse[] = [];
    const nonCanadianCourses: FlightSchoolCourse[] = [];

    coursesByPart.value[currentPart.value].forEach((course) => {
        if (isCanadianCourse(course)) {
            canadianCourses.push(course);
        } else {
            nonCanadianCourses.push(course);
        }
    });

    const result: CourseTypes[] = [];

    if (nonCanadianCourses.length > 0) {
        result.push({
            label: CourseTypeLabel.PILOT_CERTIFICATIONS,
            courses: nonCanadianCourses,
            icon: 'sym_o_flight_takeoff',
        });
    }

    if (canadianCourses.length > 0) {
        result.push({
            label: CourseTypeLabel.CANADIAN_CONVERSION,
            courses: canadianCourses,
            icon: 'fab fa-canadian-maple-leaf',
        });
    }

    return result;
});

const currentCourseTypeLabel = computed<CourseTypeLabel>(() => {
    if (splitCourses.value.length === 1) {
        return splitCourses.value[0].label;
    }

    return isCanadianCourse(props.currentCourse)
        ? CourseTypeLabel.CANADIAN_CONVERSION
        : CourseTypeLabel.PILOT_CERTIFICATIONS;
});

</script>
<template>
    <div
        id="select-course"
        :key="currentCourseTypeLabel"
        class="q-mr-sm"
    >
        <div class="course-type-list">
            <q-expansion-item
                v-for="courseType in splitCourses"
                :key="`${courseType.label}-${currentPart}`"
                switch-toggle-side
                :default-opened="currentCourseTypeLabel === courseType.label"
                :disable="splitCourses.length === 1"
                class="course-type-expansion-item"
                dense
                :content-inset-level="0.25"
            >
                <template #header>
                    <div class="row items-center">
                        <div class="col-shrink">
                            <q-icon
                                :name="courseType.icon"
                                size="24px"
                                class="q-pr-md"
                                color="black"
                            />
                        </div>
                        <div class="col">
                            <div class="course-type-label">
                                {{ courseType.label }}
                            </div>
                        </div>
                    </div>
                </template>
                <template
                    v-for="course in courseType.courses"
                    :key="course.certificate.acronym"
                >
                    <FsumNavButton
                        v-if="course.ratings.length === 1"
                        :label="course.certificate.title"
                        :to="{
                            name: 'classOverview',
                            params: {
                                schoolSlug: currentSchool.urlSlug,
                                courseAcronym: course.certificate.acronym.toLowerCase(),
                                part: currentPart,
                                ratingAcronym: course.ratings[0].acronym.toLowerCase(),
                            },
                        }"
                        :active="isCurrentRating(course.ratings[0].acronym)"
                        :aria-label="`${course.certificate.title} Class Overview`"
                        red-indicator-location="left"
                        class="q-my-md q-py-xs"
                        :class="{'text-weight-bold': isCurrentRating(course.ratings[0].acronym)}"
                        remove-extra-padding
                        dense
                    />
                    <q-expansion-item
                        v-else
                        class="course-expansion-item q-my-md"
                        :header-class="{'bg-gray': isCurrentCourse(course.certificate.acronym), 'q-pr-xs': true}"
                        header-style="border-radius: 4px;"
                        group="school-courses"
                        dense-toggle
                        dense
                        :label="course.certificate.title"
                        :default-opened="isCurrentCourse(course.certificate.acronym)"
                        :content-inset-level="0.15"
                    >
                        <nav class="course-select-nav q-pt-md q-px-sm">
                            <q-list class="course-select-list q-gutter-y-sm">
                                <FsumNavButton
                                    v-for="rating in course.ratings"
                                    :key="rating.acronym"
                                    :label="rating.title"
                                    :to="{
                                        name: 'classOverview',
                                        params: {
                                            schoolSlug: currentSchool.urlSlug,
                                            courseAcronym: course.certificate.acronym.toLowerCase(),
                                            part: currentPart,
                                            ratingAcronym: rating.acronym.toLowerCase(),
                                        },
                                    }"
                                    :active="isCurrentRating(rating.acronym)"
                                    :aria-label="`${course.certificate.title} Class Overview`"
                                    class="q-ml-sm q-py-xs q-px-sm"
                                    :class="{'text-weight-bold': isCurrentRating(rating.acronym)}"
                                    remove-extra-padding
                                    dense
                                />
                            </q-list>
                        </nav>
                    </q-expansion-item>
                </template>
            </q-expansion-item>
        </div>
    </div>
</template>
<style>
#select-course .course-type-expansion-item .q-item.disabled,
#select-course .course-type-expansion-item .q-item.disabled * {
    opacity: 1 !important;
    cursor: default !important;
}
</style>
<style scoped>
.course-type-label {
    font-size: 13px;
    font-weight: 500;
    color: var(--fsum-black);
}

.course-expansion-item {
    font-size: 14px;
    margin-left: 5px;
}

:deep(.course-type-expansion-item > .q-expansion-item__container > .q-item) {
    padding-inline: 0;
}

:deep(.q-item__section--avatar) {
    min-width: 20px;
}

/** Maintain the focus outline on the item when it is interacted with */
:deep(.q-item:focus) {
    outline: 1px solid var(--fsum-black);
}
.course-select-list {
    border-left: 2px solid var(--fsum-red4);
}
</style>
